import styled from '@emotion/styled';
import { Field, Loader } from '@hotjar/hotsauce';
import { ColFormWrapper } from '@hotjar/webapp/features/Registration/common/styles';
import type { OnboardingDetails } from '@hotjar/webapp/domains/activation/onboarding/model';
import type { UserId } from '@hotjar/user';
import { useUserExperiment } from '@hotjar/webapp/hooks/useExperiment';
import { useCurrentUser } from '@hotjar/user';
import type { USER_GOALS } from '@hotjar/webapp/domains/activation/user-personalization-info';
import { USER_EXPERTISE } from '@hotjar/webapp/domains/activation/user-personalization-info';
import { MEDIUM_OR_LARGE_COMPANY_SIZE_MIN } from '@hotjar/webapp/features/Activation/OnboardingPage/steps/OrganizationSteps/steps/PhoneNumberStep';
import { ONBOARDING_STEPS, useOnboardingNavigation } from './hooks/useOnboardingNavigation';
import type { OnboardingOrgDetails } from './hooks/useOnboardingNavigation';
import {
  OrganizationName,
  OrganizationRole,
  OrganizationSize,
  OrganizationGoal,
  ExpertiseSelection,
  PhoneNumberStep,
} from './steps';
import { useGeoLocation } from '../../hooks/useGeoLocation';
import type { OrgFinalDetails } from './steps/OrganizationRole';

export interface OrganizationStepsProps {
  userId: UserId;
  onSubmit: (onboardingDetails: OnboardingDetails) => void;
  isSubmitInProgress?: boolean;
}

export const StyledField = styled(Field)`
  width: 100%;
`;

const StyledColFormWrapper = styled(ColFormWrapper)`
  min-width: 500px;
  padding: 0;
`;

export const OrganizationSteps = ({
  userId,
  isSubmitInProgress = false,
  onSubmit,
}: OrganizationStepsProps): JSX.Element => {
  const { data: country, isLoading } = useGeoLocation();
  const { data: user } = useCurrentUser();

  const { step, goToStep, setOrgDetails, getOrgDetails } = useOnboardingNavigation();

  const canSeePhoneExperiment =
    (getOrgDetails(userId)?.organizationSizeMin || 0) >= MEDIUM_OR_LARGE_COMPANY_SIZE_MIN;

  const { inVariant1: inPhoneNumberExperiment } = useUserExperiment(
    'av_onboarding_phone_number_v3',
    user,
    canSeePhoneExperiment,
  );

  const onFormSubmit = () => {
    const DEFAULT_DETAILS = {
      organizationName: '',
      organizationSize: '',
      role: '',
      phoneNumber: '',
      country: undefined,
      subscribeToNewsletter: false,
      consentToParentCompanyRules: false,
      organizationSizeMin: 0,
    };

    onSubmit({
      ...DEFAULT_DETAILS,
      ...getOrgDetails(userId),
      country: getOrgDetails(userId)?.country || country,
    });
  };

  const saveOrgName = (name: OnboardingOrgDetails['organizationName']) => {
    setOrgDetails({ organizationName: name }, userId);
    goToStep(ONBOARDING_STEPS.ORG_SIZE);
  };

  const saveOrgSize = (size: OnboardingOrgDetails['organizationSize']) => {
    setOrgDetails({ organizationSize: size }, userId);
    goToStep(ONBOARDING_STEPS.ROLE);
  };

  const saveRole = (finalDetails: OrgFinalDetails) => {
    setOrgDetails(finalDetails, userId);
    goToStep('goal');
  };

  const saveGoal = (goal: USER_GOALS) => {
    setOrgDetails({ goal }, userId);

    goToStep(ONBOARDING_STEPS.EXPERTISE);
  };

  const saveExpertise = (expertise = USER_EXPERTISE.SKIP) => {
    setOrgDetails({ expertise }, userId);
    if (inPhoneNumberExperiment) {
      goToStep(ONBOARDING_STEPS.PHONE_NUMBER);
    } else {
      onFormSubmit();
    }
  };

  const savePhoneNumber = (phoneNumber?: OnboardingOrgDetails['phoneNumber']) => {
    setOrgDetails({ phoneNumber }, userId);
    onFormSubmit();
  };

  const renderStep = () => {
    switch (step) {
      case ONBOARDING_STEPS.ORG_NAME:
        return <OrganizationName onSubmit={saveOrgName} userId={userId} />;
      case ONBOARDING_STEPS.ORG_SIZE:
        return <OrganizationSize onSubmit={saveOrgSize} userId={userId} />;
      case ONBOARDING_STEPS.EXPERTISE:
        return (
          <ExpertiseSelection
            userId={userId}
            onSubmit={saveExpertise}
            isSubmitInProgress={isSubmitInProgress}
          />
        );
      case ONBOARDING_STEPS.ROLE:
        return (
          <OrganizationRole
            onSubmit={saveRole}
            userId={userId}
            isSubmitInProgress={isSubmitInProgress}
          />
        );
      case ONBOARDING_STEPS.GOAL:
        return (
          <OrganizationGoal
            onSubmit={saveGoal}
            userId={userId}
            isSubmitInProgress={isSubmitInProgress}
          />
        );
      case ONBOARDING_STEPS.PHONE_NUMBER:
        return (
          <PhoneNumberStep
            onSubmit={savePhoneNumber}
            userId={userId}
            isSubmitInProgress={isSubmitInProgress}
          />
        );

      default:
        return <OrganizationName onSubmit={saveOrgName} userId={userId} />;
    }
  };

  return (
    <StyledColFormWrapper data-testid="organization-steps">
      <Loader isLoading={isLoading}>{renderStep()}</Loader>
    </StyledColFormWrapper>
  );
};
